import React, { useState } from 'react';
import { SPOTICHOU_APK_URL, SPOTICHOU_CONFIG, spotichouQrUrl } from '../../data/spotichou';
import styles from './SpotichouGuide.module.css';

const { dashboardUrl, packageName, redirectUri, sha1 } = SPOTICHOU_CONFIG;

const SpotichouGuide: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <h4 className={styles.sectionHeading}>Installer l'application</h4>

        <div className={styles.download}>
          <div className={styles.qrBox}>
            <img
              src={spotichouQrUrl(240)}
              alt="QR code de téléchargement de l'APK Spotichou"
              width={120}
              height={120}
              loading="lazy"
            />
            <span>Scanne depuis le téléphone</span>
          </div>

          <div className={styles.downloadMain}>
            <p className={styles.downloadIntro}>
              Application Android à installer manuellement (hors Play Store). Chaque
              famille la branche sur <strong>son propre compte Spotify Premium</strong> :
              rien n'est partagé, rien ne quitte le téléphone.
            </p>
            <a
              href={SPOTICHOU_APK_URL}
              className={styles.downloadButton}
              target="_blank"
              rel="noopener noreferrer"
            >
              Télécharger l'APK ↓
            </a>
            <p className={styles.downloadReq}>
              Android 8+ · Spotify Premium requis · - de 10 min de mise en route
            </p>
          </div>
        </div>

        <button
          type="button"
          className={styles.toggle}
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
        >
          <span className={styles.toggleChevron} data-open={open}>›</span>
          Guide d'installation pas à pas
        </button>

        {open && (
          <div className={styles.details}>
            <div className={styles.prereq}>
              <h5>Avant de commencer</h5>
              <ul>
                <li>
                  <b>Un téléphone Android</b> (Android 8 ou plus). L'iPhone n'est pas
                  encore pris en charge.
                </li>
                <li>
                  <b>Un compte Spotify Premium</b>, indispensable pour piloter la lecture.
                </li>
                <li>
                  <b>L'application Spotify installée</b> sur ce téléphone, connectée au
                  compte Premium.
                </li>
                <li>
                  <b>Moins de 10 minutes</b>
                </li>
              </ul>
            </div>

            <ol className={styles.steps}>
              <li>
                <h5>Installer l'application</h5>
                <p>
                  Ouvre le QR code ou le bouton ci-dessus <b>depuis le navigateur du
                  téléphone</b> et télécharge le fichier <code>.apk</code>.
                </p>
                <ul>
                  <li>
                    Ouvre-le depuis les téléchargements. Android demande d'autoriser
                    l'installation depuis cette source → <b>Autoriser</b>.
                  </li>
                  <li>
                    Si Play Protect affiche « application non reconnue » →{' '}
                    <b>Installer quand même</b>.
                  </li>
                  <li>
                    Sur Xiaomi / Redmi / POCO : si l'installation est bloquée, va dans{' '}
                    <b>
                      Réglages → Applications → Autorisations spéciales → Installer des
                      applis inconnues
                    </b>{' '}
                    et autorise ton navigateur.
                  </li>
                </ul>
              </li>

              <li>
                <h5>Créer ton app Spotify</h5>
                <p>Tes identifiants à toi, sur le tableau de bord développeur de Spotify.</p>
                <ul>
                  <li>
                    Va sur <a href={dashboardUrl} target="_blank" rel="noopener noreferrer">
                      developer.spotify.com/dashboard
                    </a>{' '}
                    et connecte-toi avec ton compte Spotify, puis clique sur{' '}
                    <b>Create app</b>.
                  </li>
                  <li>
                    <b>App name</b> et <b>App description</b> : ce que tu veux (par exemple
                    « Spotichou »).
                  </li>
                  <li>
                    <b>Redirect URI</b> : <code>{redirectUri}</code>, exactement cette
                    valeur, puis <b>Add</b>.
                  </li>
                  <li>
                    <b>APIs used</b> : coche <b>Web API</b> et <b>Android</b>.
                  </li>
                  <li>
                    Une fois l'app créée, ouvre son onglet <b>Android</b> et ajoute le
                    package <code>{packageName}</code> et le SHA1 fingerprint :
                    <span className={styles.fingerprint}>{sha1}</span>
                  </li>
                  <li>
                    Dans <b>Basic Information</b>, copie le <b>Client ID</b>. C'est la seule
                    chose à reporter dans l'app.
                  </li>
                </ul>
              </li>

              <li>
                <h5>Connecter l'app à ton compte</h5>
                <p>Tout se passe dans l'espace parent, caché derrière un appui long.</p>
                <ul>
                  <li>Lance Spotichou.</li>
                  <li>
                    Sur l'écran d'accueil, <b>maintiens l'appui 5&nbsp;secondes</b> dans le
                    coin <b>haut-gauche</b> (la zone marquée « ↖ Zone parents »).
                  </li>
                  <li>
                    Onglet <b>Général</b> → carte <b>Compte développeur Spotify</b> → colle
                    le <b>Client ID</b> → <b>Enregistrer</b>.
                  </li>
                  <li>
                    Onglet <b>Contenu</b> → <b>Connecter Spotify</b> → une page Spotify
                    s'ouvre, valide avec ton compte.
                  </li>
                </ul>
                <div className={styles.gesture}>
                  <span className={styles.phone} aria-hidden="true">
                    <span className={styles.zone} />
                  </span>
                  <span>
                    Le geste parent : appui long 5&nbsp;s dans le coin haut-gauche, depuis
                    n'importe quel écran.
                  </span>
                </div>
                <p className={styles.note}>
                  <b>Normal :</b> une page « page introuvable » peut clignoter une fraction
                  de seconde au retour, l'app récupère quand même la connexion. Reviens
                  dans l'onglet Contenu, les boutons d'ajout sont apparus.
                </p>
              </li>

              <li>
                <h5>Ajouter de la musique et des histoires</h5>
                <p>
                  Depuis l'onglet <b>Contenu</b>, une fois connecté.
                </p>
                <ul>
                  <li>
                    Boutons <b>Playlist</b> et <b>Album</b> → parcours ta bibliothèque
                    Spotify → coche ce que tu veux → <b>Importer</b>.
                  </li>
                  <li>
                    Tout arrive dans <b>Musique</b>. Pour déplacer un contenu vers les
                    histoires, tape son badge de catégorie.
                  </li>
                  <li>
                    <b>Visible</b> masque un contenu à l'enfant sans le supprimer ;{' '}
                    <b>Choix</b> autorise l'enfant à choisir un titre précis dans la
                    playlist ; la corbeille supprime définitivement.
                  </li>
                </ul>
              </li>

              <li>
                <h5>Régler l'usage</h5>
                <p>
                  Onglet <b>Général</b> de l'espace parent.
                </p>
                <ul>
                  <li>
                    <b>Volume maximum</b> : un plafond appliqué à la lecture, pour protéger
                    l'audition.
                  </li>
                  <li>
                    <b>Minuterie de sommeil</b> : coupe la musique en fondu après le délai
                    choisi.
                  </li>
                  <li>
                    <b>Mode nuit</b> : interface en tons sombres pendant la plage horaire
                    définie (histoire du soir).
                  </li>
                  <li>
                    L'onglet <b>Stats</b> montre le temps d'écoute des 7 derniers jours.
                  </li>
                </ul>
              </li>

              <li>
                <h5>Empêcher l'enfant de quitter l'app</h5>
                <p>
                  Optionnel, mais pratique : Spotichou gère elle-même le mode kiosque, il
                  suffit d'activer l'épinglage d'écran d'Android.
                </p>
                <ul>
                  <li>
                    Va dans <b>Réglages Android → Sécurité → Épinglage d'écran</b> (ou
                    « Épingler des applications ») et active l'option.
                  </li>
                  <li>
                    C'est tout : au lancement, Spotichou détecte le réglage et épingle son
                    propre écran.
                  </li>
                  <li>
                    Pour sortir : le geste « retour + récents » maintenu, propre à ton
                    téléphone.
                  </li>
                </ul>
                <p className={styles.note}>
                  Sans épinglage, l'app fonctionne quand même, l'enfant peut juste en
                  sortir avec le bouton d'accueil.
                </p>
              </li>
            </ol>

            <div className={styles.trouble}>
              <h5>Si ça coince</h5>
              <dl>
                <dt>Le bouton « Connecter Spotify » ne réagit pas</dt>
                <dd>
                  Le Client ID n'a pas encore été enregistré. Retourne dans l'onglet
                  Général, colle-le et appuie sur Enregistrer.
                </dd>
                <dt>Bandeau orange « Spotify non disponible »</dt>
                <dd>
                  L'application Spotify n'est pas ouverte, ou aucun compte Premium n'y est
                  connecté. Ouvre Spotify, connecte-toi, reviens sur Spotichou : la
                  connexion se rétablit toute seule au bout de quelques secondes.
                </dd>
                <dt>La lecture ne démarre pas quand l'enfant tape une vignette</dt>
                <dd>
                  Spotify Connect a besoin d'un appareil actif : ouvre d'abord
                  l'application Spotify sur ce téléphone, puis lance la lecture depuis
                  Spotichou.
                </dd>
                <dt>Erreur au moment de la connexion Spotify</dt>
                <dd>
                  Vérifie, sur le dashboard Spotify : le Client ID collé sans espace ni
                  retour à la ligne ; le Redirect URI exactement égal à{' '}
                  <code>{redirectUri}</code> ; le package <code>{packageName}</code> et le
                  SHA1 bien enregistrés dans l'onglet Android.
                </dd>
              </dl>
            </div>

            <div className={styles.closing}>
              <h5>Bon à savoir</h5>
              <ul>
                <li>Android uniquement pour l'instant, pas de version iPhone.</li>
                <li>
                  Aucune donnée ne quitte le téléphone : contenu, réglages et statistiques
                  restent en local.
                </li>
                <li>
                  L'app se met à jour toute seule pour les corrections courantes. Une
                  nouvelle version majeure passe par un nouveau lien.
                </li>
                <li>
                  La configuration (Client ID, playlists, réglages) survit aux mises à jour
                  de l'app.
                </li>
              </ul>
            </div>

            <p className={styles.legal}>
              Spotichou · usage personnel · le SDK et l'API Spotify restent soumis aux
              Spotify Developer Terms.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpotichouGuide;
